import React, { useState, useEffect } from 'react';
import { api } from '../utils/api';
import Modal from './Modal';
import { useApp } from '../context/AppContext';

/**
 * Hierarchical Location Selector
 * @param {Function} onChange - Returns a string like "Country > Division > District > Area"
 * @param {string} initialValue - Existing value to parse
 */
export default function LocationSelector({ onChange, initialValue }) {
  const { t } = useApp();
  const [tree, setTree] = useState([]);
  const [selected, setSelected] = useState({ country: null, division: null, district: null, area: null });
  const [showModal, setShowModal] = useState(false);
  const [tempSelected, setTempSelected] = useState({ country: null, division: null, district: null, area: null });

  useEffect(() => {
    api.get('/locations?tree=1')
      .then(res => setTree(Array.isArray(res.locations) ? res.locations : []))
      .catch(err => {
        console.error('Failed to load locations:', err);
        setTree([]);
      });
  }, []);

  useEffect(() => {
    if (typeof initialValue === 'string' && initialValue.trim() !== '' && Array.isArray(tree) && tree.length > 0) {
      try {
        const parts = initialValue.split(' > ');
        const country = tree.find(l => l && l.name === parts[0]) || null;
        const division = country?.children?.find(l => l && l.name === parts[1]) || null;
        const district = division?.children?.find(l => l && l.name === parts[2]) || null;
        const area = district?.children?.find(l => l && l.name === parts[3]) || null;

        setSelected({ country, division, district, area });
      } catch (e) {
        console.error('Error parsing initial location:', e);
      }
    } else if (typeof initialValue === 'string' && initialValue.trim() === '') {
      setSelected({ country: null, division: null, district: null, area: null });
    }
  }, [initialValue, tree]);


  const handleChange = (level, val) => {
    if (!Array.isArray(tree)) return;
    const newSelected = { ...tempSelected };
    const id = parseInt(val);

    if (level === 'country') {
      newSelected.country = tree.find(l => l && l.id === id) || null;
      newSelected.division = null;
      newSelected.district = null;
      newSelected.area = null;
    } else if (level === 'division') {
      newSelected.division = tempSelected.country?.children?.find(l => l && l.id === id) || null;
      newSelected.district = null;
      newSelected.area = null;
    } else if (level === 'district') {
      newSelected.district = tempSelected.division?.children?.find(l => l && l.id === id) || null;
      newSelected.area = null;
    } else if (level === 'area') {
      newSelected.area = tempSelected.district?.children?.find(l => l && l.id === id) || null;
    }

    setTempSelected(newSelected);
  };

  const handleConfirm = () => {
    setSelected(tempSelected);
    if (typeof onChange === 'function') {
      const parts = [
        tempSelected.country?.name,
        tempSelected.division?.name,
        tempSelected.district?.name,
        tempSelected.area?.name
      ].filter(Boolean);
      onChange(parts.join(' > '));
    }
    setShowModal(false);
  };

  const currentLocation = [
    selected.country?.name,
    selected.division?.name,
    selected.district?.name,
    selected.area?.name
  ].filter(Boolean).join(' > ') || 'Select Location';

  const openModal = () => {
    setTempSelected(selected);
    setShowModal(true);
  };

  return (
    <>
      <div 
        className="form-input" 
        style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: 'var(--surface)' }}
        onClick={openModal}
      >
        <span style={{ color: selected.country ? 'var(--text)' : 'var(--text3)' }}>
          📍 {currentLocation}
        </span>
        <span style={{ fontSize: '10px', color: 'var(--text3)' }}>▼</span>
      </div>

      <Modal open={showModal} onClose={() => setShowModal(false)} title="Select Location">
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', padding: '4px' }}>
          <div className="form-group">
            <label className="form-label">Country</label>
            <select className="form-input" value={tempSelected.country?.id || ''} onChange={e => handleChange('country', e.target.value)}>
              <option value="">-- Select Country --</option>
              {Array.isArray(tree) && tree.map(l => l && <option key={l.id} value={l.id}>{l.name}</option>)}
            </select>
          </div>

          <div className="form-group">
            <label className="form-label">Division</label>
            <select className="form-input" disabled={!tempSelected.country} value={tempSelected.division?.id || ''} onChange={e => handleChange('division', e.target.value)}>
              <option value="">-- Select Division --</option>
              {tempSelected.country?.children?.map(l => l && <option key={l.id} value={l.id}>{l.name}</option>)}
            </select>
          </div>

          <div className="form-group">
            <label className="form-label">District</label>
            <select className="form-input" disabled={!tempSelected.division} value={tempSelected.district?.id || ''} onChange={e => handleChange('district', e.target.value)}>
              <option value="">-- Select District --</option>
              {tempSelected.division?.children?.map(l => l && <option key={l.id} value={l.id}>{l.name}</option>)}
            </select>
          </div>

          <div className="form-group">
            <label className="form-label">Area</label>
            <select className="form-input" disabled={!tempSelected.district} value={tempSelected.area?.id || ''} onChange={e => handleChange('area', e.target.value)}>
              <option value="">-- Select Area --</option>
              {tempSelected.district?.children?.map(l => l && <option key={l.id} value={l.id}>{l.name}</option>)}
            </select>
          </div>

          <div style={{ marginTop: '12px', display: 'flex', gap: '8px' }}>
            <button className="btn btn-outline btn-full" onClick={() => setShowModal(false)}>Cancel</button>
            <button className="btn btn-primary btn-full" onClick={handleConfirm}>Confirm</button>
          </div>
        </div>
      </Modal>
    </>
  );
}

